/**
 * AI聊天主页面
 * 数字张雪峰AI高考志愿填报咨询系统
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSquarePlus, RefreshCw, X, Maximize2, Minimize2, Loader2, AlertCircle, Cpu } from 'lucide-react';
import { StudentInfoCard } from '@/components/chat/StudentInfoCard';
import { MobileStudentInfoCard } from '@/components/chat/MobileStudentInfoCard';
import MessageList from '@/components/chat/MessageList';
import InputArea from '@/components/chat/InputArea';
import WelcomeScreen from '@/components/chat/WelcomeScreen';
import AIChatSkeleton from '@/components/chat/AIChatSkeleton';
import { ErrorCard, NetworkError } from '@/components/ui/error-boundary';
import { useToast, useChatToast, confirmDialog } from '@/components/ui/toast';
import { parseAxiosError, ApiError } from '@/utils/error-handler.tsx';
import * as chatAPI from '@/api/chat';
import { setCurrentStudentId } from '@/api/chat';
import type { StudentInfo } from '@/api/chat';
import { useStudent } from '@/context/StudentContext';
import { useAuth } from '@/context/AuthContext';
import { aiApi, scoresApi } from '@/api';
import { calculateCompletionRate } from '@/utils/completion-rate';

import type { PageId } from '@/App';

interface AIChatProps {
  onNavigate: (pageId: PageId) => void;
}

/** 将学生档案的 target_level 映射到 StudentInfo 的 targetUniversityLevel */
function mapTargetLevel(level: string): StudentInfo['targetUniversityLevel'] {
  const map: Record<string, StudentInfo['targetUniversityLevel']> = {
    '985': '985',
    '211': '211',
    '985/211': '985',
    '双一流': '双一流',
    '一本': '双一流',
    '普通本科': '普通本科',
    '二本': '普通本科',
    '三本': '普通本科',
    '专科': '专科',
    '高职': '专科',
    // 初中
    '重点高中': '985',
    '一级达标': '211',
    '二级达标': '双一流',
    '普通高中': '普通本科',
    '职高/中专': '专科',
  };
  return map[level] || undefined;
}

/** 将学生档案的 subject_type（中文）映射到 StudentInfo 的 stream */
function mapStreamType(subjectType: string): StudentInfo['stream'] {
  if (subjectType === '历史类' || subjectType === 'history') return 'history';
  return 'physics'; // 物理类 / physics / 空值默认物理
}

export default function AIChat({ onNavigate }: AIChatProps) {
  // Toast 反馈
  const toast = useToast();
  const chatToast = useChatToast();
  
  // 获取当前学生
  const { currentStudent } = useStudent();
  const { isAuthenticated } = useAuth();
  
  // 会话状态
  const [sessions, setSessions] = useState<chatAPI.ChatSession[]>([]);
  const [currentSession, setCurrentSession] = useState<chatAPI.ChatSession | null>(null);
  const [messages, setMessages] = useState<chatAPI.ChatMessage[]>([]);
  
  // 加载状态
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // 错误状态
  const [error, setError] = useState<ApiError | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // 学生信息状态
  const [studentInfo, setStudentInfo] = useState<StudentInfo>({});
  const [completionRate, setCompletionRate] = useState(0);
  
  // UI状态
  const [showWelcome, setShowWelcome] = useState(true);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  // LLM 配置状态
  const [llmConfigured, setLlmConfigured] = useState<boolean | null>(null);
  const [llmSource, setLlmSource] = useState<string>('');

  // 设置当前学生ID到API模块
  useEffect(() => {
    if (currentStudent?.id) {
      setCurrentStudentId(currentStudent.id);
    }
  }, [currentStudent]);

  // 加载 LLM 配置状态
  useEffect(() => {
    if (isAuthenticated) {
      aiApi.configStatus()
        .then(res => {
          setLlmConfigured(res.data.configured);
          setLlmSource(res.data.source || '');
        })
        .catch(() => { /* ignore */ });
    }
  }, [isAuthenticated]);

  // 从学生档案自动填充 studentInfo（始终从档案获取最新数据）
  // 当 currentStudent.id 变化时重新填充（包括从 null 到有值的情况）
  useEffect(() => {
    if (!currentStudent?.id) return;

    const mapStudentInfo = async () => {
      const info: StudentInfo = {
        name: currentStudent.name,
        school: currentStudent.school,
        grade: currentStudent.grade,
        examType: currentStudent.education_level,
        stream: mapStreamType(currentStudent.subject_type),
        targetUniversityLevel: mapTargetLevel(currentStudent.target_level),
      };

      // 从 latest_score 获取总分和排名
      if (currentStudent.latest_score) {
        info.totalScore = currentStudent.latest_score.total_score;
        info.provinceRanking = currentStudent.latest_score.grade_rank;
      }

      // 从成绩 API 获取最新一次的科目分数
      try {
        const res = await scoresApi.list({ student_id: currentStudent.id, latest: 1 });
        if (res.data?.data?.length > 0) {
          const score = res.data.data[0];
          info.chineseScore = score.chinese_score;
          info.mathScore = score.math_score;
          info.englishScore = score.english_score;
          info.physicsScore = score.physics_score;
          info.chemistryScore = score.chemistry_score;
          info.geographyScore = score.geography_score;
          info.historyScore = score.history_score;
          info.biologyScore = score.biology_score;
          info.politicsScore = score.politics_score;
          if (!info.totalScore) info.totalScore = score.total_score;
        }
      } catch {
        // 静默失败，使用已有数据
      }

      setStudentInfo(info);
      const rate = calculateCompletionRate(info) * 100;
      console.log('[AIChat] Auto-populate studentInfo:', info, 'completionRate:', rate);
      setCompletionRate(rate);
    };

    mapStudentInfo();
  }, [currentStudent?.id]);

  // 加载会话列表
  useEffect(() => {
    if (currentStudent?.id) {
      loadSessions();
    } else {
      setInitialLoading(false);
      // 游客模式不显示错误
      if (isAuthenticated) {
        setError({
          message: '请先选择学生',
          code: 'NO_STUDENT'
        });
      }
    }
  }, [currentStudent]);

  // 加载当前会话的消息
  useEffect(() => {
    if (currentSession) {
      loadMessages(currentSession.id);
      // 新会话（无消息）：显示欢迎页
      // 已有对话的会话：隐藏欢迎页
      chatAPI.getMessages(currentSession.id).then(msgs => {
        setShowWelcome(msgs.length === 0);
      }).catch(() => {});
      // 完成度由 studentInfo 驱动，不再使用会话中的旧数据
    }
  }, [currentSession]);

  const loadSessions = async () => {
    try {
      setInitialLoading(true);
      setError(null);
      const sessions = await chatAPI.getSessions();
      setSessions(sessions);
      setRetryCount(0);
      
      // 如果有会话，自动加载第一个
      if (sessions.length > 0) {
        setCurrentSession(sessions[0]);
      }
    } catch (error) {
      console.error('加载会话列表失败:', error);
      const parsedError = parseAxiosError(error);
      setError(parsedError);
      
      // 使用Toast显示错误
      toast.addToast({
        title: '加载失败',
        message: parsedError.message,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setInitialLoading(false);
    }
  };

  const loadMessages = async (sessionId: number) => {
    try {
      const messages = await chatAPI.getMessages(sessionId);
      setMessages(messages);
    } catch (error) {
      console.error('加载消息失败:', error);
      const parsedError = parseAxiosError(error);
      setError(parsedError);
      
      // 静默失败，不显示Toast，因为页面已显示错误状态
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setError(null);
    loadSessions();
  };

  const handleSend = async (message: string) => {
    if (loading || !message.trim()) return;

    try {
      setLoading(true);
      
      // 如果没有当前会话，创建一个
      let sessionId = currentSession?.id;
      if (!sessionId) {
        const newSession = await chatAPI.createSession(studentInfo);
        setCurrentSession(newSession);
        setSessions([newSession, ...sessions]);
        sessionId = newSession.id;
      }

      // 发送消息
      const response = await chatAPI.sendMessage({
        sessionId,
        message,
        studentInfo,
      });

      // 更新消息列表
      setMessages([...messages, response.message]);
      
      // 更新会话信息
      if (currentSession) {
        setCurrentSession({
          ...currentSession,
          ...response.session,
        });
      }

      // 合并后端提取的新信息到 studentInfo（实时更新侧边栏）
      if (response.session.studentInfo) {
        const backendInfo = response.session.studentInfo;
        setStudentInfo(prev => {
          const merged: StudentInfo = { ...prev };
          // 合并后端提取的字段
          if (backendInfo.name) merged.name = backendInfo.name;
          if (backendInfo.school) merged.school = backendInfo.school;
          if (backendInfo.grade) merged.grade = backendInfo.grade;
          if (backendInfo.stream) merged.stream = backendInfo.stream;
          if (backendInfo.totalScore) merged.totalScore = backendInfo.totalScore;
          if (backendInfo.targetCities) merged.targetCities = backendInfo.targetCities;
          if (backendInfo.preferredMajors) merged.preferredMajors = backendInfo.preferredMajors;
          if (backendInfo.targetUniversityLevel) merged.targetUniversityLevel = backendInfo.targetUniversityLevel;
          // 重新计算完整度（×100 转为百分比）
          setCompletionRate(calculateCompletionRate(merged) * 100);
          return merged;
        });
      } else if (response.session.completionRate) {
        // 如果没有 studentInfo，使用后端计算的完整度（×100 转为百分比）
        setCompletionRate(response.session.completionRate * 100);
      }

      // 更新会话列表中的标题
      setSessions(sessions.map(session => 
        session.id === response.session.id ? response.session : session
      ));

      setShowWelcome(false);
      
      // 显示成功Toast
      toast.addToast({
        title: '发送成功',
        message: '消息已发送给AI',
        type: 'success',
        duration: 2000,
      });
      
      // 如果AI有响应，显示正在处理Toast
      chatToast.aiThinking();
    } catch (error) {
      console.error('发送消息失败:', error);
      const parsedError = parseAxiosError(error);
      
      toast.addToast({
        title: '发送失败',
        message: parsedError.message,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = async () => {
    try {
      const newSession = await chatAPI.createSession(studentInfo);
      setCurrentSession(newSession);
      setSessions([newSession, ...sessions]);
      setMessages([]);
      setShowWelcome(true);
      setMobileSidebarOpen(false);
      
      // 显示成功Toast
      toast.addToast({
        title: '创建成功',
        message: '新会话已创建',
        type: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('创建新会话失败:', error);
      const parsedError = parseAxiosError(error);
      
      toast.addToast({
        title: '创建失败',
        message: parsedError.message,
        type: 'error',
        duration: 5000,
      });
    }
  };

  const handleClearChat = () => {
    confirmDialog.clearChat(() => {
      setMessages([]);
      setShowWelcome(true);
    });
  };

  const handleDeleteSession = async (sessionId: number) => {
    confirmDialog.deleteSession(async () => {
      try {
        await chatAPI.deleteSession(sessionId);
        
        // 从列表中移除
        const newSessions = sessions.filter(s => s.id !== sessionId);
        setSessions(newSessions);
        
        // 如果删除的是当前会话，切换到其他会话或创建新会话
        if (currentSession?.id === sessionId) {
          if (newSessions.length > 0) {
            setCurrentSession(newSessions[0]);
            toast.addToast({
              title: '已切换',
              message: '已切换到其他会话',
              type: 'info',
              duration: 2000,
            });
          } else {
            setCurrentSession(null);
            setMessages([]);
            setShowWelcome(true);
          }
        }
        
        // 显示删除成功Toast
        toast.addToast({
          title: '删除成功',
          message: '会话已删除',
          type: 'success',
          duration: 2000,
        });
      } catch (error) {
        console.error('删除会话失败:', error);
        const parsedError = parseAxiosError(error);
        
        toast.addToast({
          title: '删除失败',
          message: parsedError.message,
          type: 'error',
          duration: 5000,
        });
      }
    });
  };

  const handleStartWithQuestion = (question: string) => {
    handleSend(question);
  };

  const handleEditStudentInfo = () => {
    // TODO: 打开编辑学生信息的对话框
    alert('编辑学生信息功能即将上线');
  };

  if (initialLoading && !error) {
    return <AIChatSkeleton />;
  }

  // 显示网络错误
  if (error && retryCount < 3) {
    return (
      <div className="flex h-screen bg-gray-50">
        <main className="flex-1 flex flex-col items-center justify-center p-4">
          <NetworkError onRetry={handleRetry} />
        </main>
      </div>
    );
  }

  // 超过重试次数显示完整错误
  if (error && retryCount >= 3) {
    return (
      <div className="flex h-screen bg-gray-50">
        <main className="flex-1 flex flex-col items-center justify-center p-4">
          <ErrorCard
            title="无法连接到AI服务"
            message="请检查网络连接或稍后重试"
            error={error}
            onRetry={handleRetry}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* 左侧边栏 - 学生信息（仅登录用户可见） */}
      {isAuthenticated && (
      <>
      <aside className={`
        fixed md:relative z-30 h-full
        w-80 bg-white border-r
        transition-transform duration-300 ease-in-out
        ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}>
        <div className="flex flex-col h-full">
          {/* 顶部按钮 */}
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold text-lg">学生信息</h2>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
                className="hidden md:flex"
              >
                {isSidebarExpanded ? (
                  <Minimize2 className="w-4 h-4" />
                ) : (
                  <Maximize2 className="w-4 h-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileSidebarOpen(false)}
                className="md:hidden"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* 学生信息卡片 */}
          <div className="flex-1 overflow-y-auto p-3 md:p-4">
            {/* 桌面端使用原组件 */}
            <div className="hidden md:block">
              <StudentInfoCard
                studentInfo={studentInfo}
                completionRate={completionRate}
                isExpanded={isSidebarExpanded}
                onEdit={handleEditStudentInfo}
                onToggle={() => setIsSidebarExpanded(!isSidebarExpanded)}
                educationLevel={currentStudent?.education_level}
              />
            </div>
            {/* 移动端使用优化组件 */}
            <div className="md:hidden">
              <MobileStudentInfoCard
                studentInfo={studentInfo}
                completionRate={completionRate}
                isExpanded={isSidebarExpanded}
                onEdit={handleEditStudentInfo}
                onToggle={() => setIsSidebarExpanded(!isSidebarExpanded)}
                educationLevel={currentStudent?.education_level}
              />
            </div>
          </div>

          {/* 会话列表 */}
          {isSidebarExpanded && (
            <div className="border-t p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-sm text-gray-600">历史会话</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleNewChat}
                >
                  <MessageSquarePlus className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {sessions.map(session => (
                  <div
                    key={session.id}
                    className={`
                      p-3 rounded-lg cursor-pointer transition-colors
                      ${currentSession?.id === session.id 
                        ? 'bg-blue-50 border border-blue-200' 
                        : 'hover:bg-gray-50 border border-transparent'
                      }
                    `}
                    onClick={() => setCurrentSession(session)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {session.title || '新对话'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {chatAPI.formatDate(session.updatedAt)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(session.id);
                        }}
                        className="h-6 w-6 p-0 opacity-0 hover:opacity-100 group-hover:opacity-100"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* 移动端遮罩 */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}
      </>
      )}

      {/* 主聊天区域 */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* 顶部导航 */}
        <header className="bg-white border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileSidebarOpen(true)}
              className="md:hidden"
            >
              <MessageSquarePlus className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-semibold">AI 对话</h1>
              {currentSession && (
                <p className="text-xs text-gray-500">
                  {currentSession.title || '新对话'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadSessions()}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </header>

        {/* 游客提示 */}
        {!isAuthenticated && (
          <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 text-sm text-blue-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            登录后可保存对话记录和查看完整功能
          </div>
        )}

        {/* LLM 配置状态提示 */}
        {isAuthenticated && llmSource === 'system' && (
          <div className="bg-amber-50 border-b border-amber-100 px-4 py-2 text-sm text-amber-700 flex items-center gap-2">
            <Cpu className="w-4 h-4 flex-shrink-0" />
            当前使用系统默认 AI 配置。
            <button onClick={() => onNavigate('llm-settings')} className="underline font-medium hover:text-amber-900">前往 AI 设置</button>
            配置你自己的 API Key
          </div>
        )}

        {/* 聊天内容区域 */}
        {showWelcome ? (
          <WelcomeScreen
            onStartChat={handleStartWithQuestion}
            educationLevel={currentStudent?.education_level}
            studentInfo={studentInfo}
          />
        ) : (
          <MessageList messages={messages} loading={loading} />
        )}

        {/* 输入区域 */}
        <InputArea
          loading={loading}
          onSend={handleSend}
          onNewChat={handleNewChat}
          onClearChat={handleClearChat}
          placeholder={
            showWelcome
              ? (studentInfo.totalScore
                ? `输入您的问题，例如：我的${studentInfo.totalScore}分能上什么学校？`
                : (currentStudent?.education_level === 'junior'
                  ? "输入您的问题，例如：我的成绩可以报什么高中？"
                  : "输入您的问题，例如：我的成绩可以报什么学校？"))
              : "继续对话..."
          }
        />
      </main>
    </div>
  );
}
